/**
 * Make & Model Editor.
 *
 * Enterprise-grade management component for makes/manufacturers and their
 * models. Each make is an expandable row showing:
 * - Name, applicable category pills, model count
 * - Expanded: editable fields, category multi-select, inline model list
 *
 * Models are managed as sub-items of their parent make, establishing the
 * relationship between manufacturers and their product lines.
 */

import { useState } from "react";
import { ChevronDown, ChevronRight, Plus, Trash2, X } from "lucide-react";
import type { CategoryOption, MakeOption, ModelOption } from "@/types/settings";

interface MakeEditorProps {
  makes: MakeOption[];
  models: ModelOption[];
  categories: CategoryOption[];
  onMakesChange: (makes: MakeOption[]) => void;
  onModelsChange: (models: ModelOption[]) => void;
}

function flattenCategories(
  cats: CategoryOption[],
): Array<{ value: string; label: string }> {
  const result: Array<{ value: string; label: string }> = [];
  for (const cat of cats) {
    result.push({ value: cat.value, label: cat.label });
    if (cat.children) {
      result.push(...flattenCategories(cat.children));
    }
  }
  return result;
}

function MakeEditor({
  makes,
  models,
  categories,
  onMakesChange,
  onModelsChange,
}: MakeEditorProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [newMakeName, setNewMakeName] = useState("");
  const flatCats = flattenCategories(categories);

  const addMake = () => {
    if (!newMakeName.trim()) return;
    const id = `make-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    onMakesChange([...makes, { id, name: newMakeName.trim(), categories: [] }]);
    setNewMakeName("");
    setExpandedId(id);
  };

  const deleteMake = (id: string) => {
    const make = makes.find((m) => m.id === id);
    onMakesChange(makes.filter((m) => m.id !== id));
    if (make) {
      onModelsChange(
        models.filter((m) => m.make?.toLowerCase() !== make.name.toLowerCase()),
      );
    }
  };

  const updateMake = (id: string, updates: Partial<MakeOption>) => {
    onMakesChange(makes.map((m) => (m.id === id ? { ...m, ...updates } : m)));
  };

  const toggleCategory = (makeId: string, catValue: string) => {
    const make = makes.find((m) => m.id === makeId);
    if (!make) return;
    const cats = make.categories || [];
    const next = cats.includes(catValue)
      ? cats.filter((c) => c !== catValue)
      : [...cats, catValue];
    updateMake(makeId, { categories: next });
  };

  const getModelsForMake = (makeName: string) =>
    models.filter((m) => m.make?.toLowerCase() === makeName.toLowerCase());

  const addModel = (makeName: string, modelName: string) => {
    onModelsChange([...models, { make: makeName, model: modelName }]);
  };

  const deleteModel = (makeName: string, modelName: string) => {
    onModelsChange(
      models.filter(
        (m) =>
          !(
            m.make?.toLowerCase() === makeName.toLowerCase() &&
            m.model === modelName
          ),
      ),
    );
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-semibold text-[var(--text-primary)]">
            Makes & Models
          </h3>
          <p className="text-xs text-[var(--text-tertiary)]">
            Define manufacturers and their product lines. Link makes to
            applicable categories.
          </p>
        </div>
        <span className="text-xs text-[var(--text-tertiary)] tabular-nums">
          {makes.length} make{makes.length !== 1 ? "s" : ""} · {models.length}{" "}
          model{models.length !== 1 ? "s" : ""}
        </span>
      </div>

      {/* Make list */}
      <div className="space-y-1">
        {makes.map((make) => {
          const isExpanded = expandedId === make.id;
          const makeModels = getModelsForMake(make.name);

          return (
            <div
              key={make.id}
              className={`rounded-[var(--radius-md)] border transition-colors ${
                isExpanded
                  ? "border-[var(--color-interactive-primary)]/30 bg-[var(--surface-base)]"
                  : "border-[var(--border-subtle)] hover:border-[var(--border-default)]"
              }`}
            >
              {/* Collapsed row */}
              <div
                className="flex items-center gap-3 px-3 py-2.5 cursor-pointer"
                onClick={() => setExpandedId(isExpanded ? null : make.id)}
              >
                {isExpanded ? (
                  <ChevronDown className="h-3.5 w-3.5 text-[var(--text-tertiary)] shrink-0" />
                ) : (
                  <ChevronRight className="h-3.5 w-3.5 text-[var(--text-tertiary)] shrink-0" />
                )}
                <span className="text-sm font-medium text-[var(--text-primary)] flex-1">
                  {make.name}
                </span>
                {/* Category pills */}
                {make.categories && make.categories.length > 0 && (
                  <div className="flex items-center gap-1">
                    {make.categories.slice(0, 3).map((cv) => {
                      const cat = flatCats.find((c) => c.value === cv);
                      return (
                        <span
                          key={cv}
                          className="rounded-full bg-brand-100 px-2 py-0.5 text-[10px] font-medium text-brand-700 dark:bg-brand-900/30 dark:text-brand-300"
                        >
                          {cat?.label || cv}
                        </span>
                      );
                    })}
                    {make.categories.length > 3 && (
                      <span className="text-[10px] text-[var(--text-tertiary)]">
                        +{make.categories.length - 3}
                      </span>
                    )}
                  </div>
                )}
                <span className="text-[10px] text-[var(--text-tertiary)] tabular-nums shrink-0">
                  {makeModels.length} model
                  {makeModels.length !== 1 ? "s" : ""}
                </span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteMake(make.id);
                  }}
                  className="p-1 text-[var(--text-tertiary)] hover:text-[var(--text-danger)] transition-colors shrink-0"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>

              {/* Expanded detail */}
              {isExpanded && (
                <div className="border-t border-[var(--border-subtle)] px-4 py-3 space-y-4">
                  {/* Categories */}
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)] mb-2">
                      Applicable Categories
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {flatCats.map((cat) => {
                        const isSelected = (make.categories || []).includes(
                          cat.value,
                        );
                        return (
                          <button
                            key={cat.value}
                            onClick={() => toggleCategory(make.id, cat.value)}
                            className={`rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors ${
                              isSelected
                                ? "bg-brand-500 text-white"
                                : "bg-[var(--surface-sunken)] text-[var(--text-secondary)] hover:bg-[var(--surface-base)] border border-[var(--border-subtle)]"
                            }`}
                          >
                            {cat.label}
                          </button>
                        );
                      })}
                    </div>
                    {(!make.categories || make.categories.length === 0) && (
                      <p className="text-[10px] text-[var(--text-tertiary)] italic mt-1">
                        No categories selected — this make appears for all
                        categories.
                      </p>
                    )}
                  </div>

                  {/* Models */}
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)] mb-2">
                      Models
                    </p>
                    {makeModels.length > 0 ? (
                      <div className="flex flex-wrap gap-1.5 mb-2">
                        {makeModels.map((m) => (
                          <span
                            key={m.model}
                            className="inline-flex items-center gap-1 rounded-[var(--radius-sm)] bg-[var(--surface-sunken)] px-2 py-1 text-xs text-[var(--text-primary)]"
                          >
                            {m.model}
                            <button
                              onClick={() => deleteModel(make.name, m.model)}
                              className="text-[var(--text-tertiary)] hover:text-[var(--text-danger)]"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="text-[10px] text-[var(--text-tertiary)] italic mb-2">
                        No models defined yet.
                      </p>
                    )}
                    <AddModelForm onAdd={(name) => addModel(make.name, name)} />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Add make form */}
      <div className="mt-3 flex items-center gap-2">
        <input
          type="text"
          value={newMakeName}
          onChange={(e) => setNewMakeName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") addMake();
          }}
          placeholder="e.g., John Deere"
          className="flex-1 h-8 rounded-[var(--radius-sm)] border border-[var(--border-default)] bg-[var(--surface-base)] px-2.5 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus-visible:shadow-[var(--focus-ring)]"
        />
        <button
          onClick={addMake}
          disabled={!newMakeName.trim()}
          className="flex h-8 w-8 items-center justify-center rounded-[var(--radius-sm)] border border-[var(--border-default)] text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--surface-sunken)] disabled:opacity-40 transition-colors"
        >
          <Plus className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

/** Inline form for adding a model to a make. */
function AddModelForm({ onAdd }: { onAdd: (name: string) => void }) {
  const [name, setName] = useState("");

  const handleAdd = () => {
    if (!name.trim()) return;
    onAdd(name.trim());
    setName("");
  };

  return (
    <div className="flex items-center gap-2">
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") handleAdd();
        }}
        placeholder="Model name"
        className="flex-1 h-7 rounded-[var(--radius-sm)] border border-[var(--border-default)] bg-[var(--surface-base)] px-2 text-xs text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus-visible:shadow-[var(--focus-ring)]"
      />
      <button
        onClick={handleAdd}
        disabled={!name.trim()}
        className="flex h-7 items-center gap-1 rounded-[var(--radius-sm)] px-2 text-[11px] font-medium text-[var(--color-interactive-primary)] hover:bg-brand-50 disabled:opacity-40 transition-colors dark:hover:bg-brand-900/20"
      >
        <Plus className="h-3 w-3" /> Add
      </button>
    </div>
  );
}

export default MakeEditor;
